import { tr } from 'i18n';
import { useRef, useState, useCallback, useEffect } from 'react';
import jsQR from 'jsqr';

/**
 * Хук для управления камерой: запуск, остановка, переключение, QR-сканирование.
 * POCO C85 совместимый (retry, camera2 0 detection, localStorage preferred camera).
 *
 * @param {Object} opts
 * @param {boolean}  opts.showQr          — модалка QR открыта?
 * @param {Function} opts.onNotify        — fn({ isOpen, message, type })
 * @param {Function} opts.onQRCodeScanned — fn(qrData)
 */
const useCamera = ({ showQr, onNotify, onQRCodeScanned }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanningRef = useRef(false);
  const lastScanTimeRef = useRef(0);
  const cameraIndexRef = useRef(-1);
  const videoDevicesRef = useRef([]);

  const [availableCameras, setAvailableCameras] = useState([]);
  const [currentCameraInfo, setCurrentCameraInfo] = useState(null);
  const [isCameraLoading, setIsCameraLoading] = useState(false);

  // ---- QR-сканирование (requestAnimationFrame loop) ----
  const scanQRCode = useCallback(() => {
    if (!scanningRef.current || !videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code && code.data) {
        const now = Date.now();
        if (now - lastScanTimeRef.current > 2000) {
          lastScanTimeRef.current = now;
          if (onQRCodeScanned) onQRCodeScanned(code.data);
        }
      }
    }

    if (scanningRef.current) {
      requestAnimationFrame(scanQRCode);
    }
  }, [onQRCodeScanned]);

  // ---- Запуск камеры ----
  const startCamera = useCallback(async (manualDeviceId = null) => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(tr("API камеры не поддерживается в этом браузере"));
      }

      setIsCameraLoading(true);
      let stream = null;

      // ==== ПЕРЕКЛЮЧЕНИЕ / РУЧНОЙ ВЫБОР ====
      // Определяем, какую камеру открывать. Если manualDeviceId передан — строго её. 
      // Иначе — по индексу, если он есть.
      const targetDeviceId = manualDeviceId || (
          cameraIndexRef.current !== -1 && videoDevicesRef.current.length > 0
          ? videoDevicesRef.current[cameraIndexRef.current]?.deviceId
          : null
      );

      if (targetDeviceId) {
        console.log('🎯 Строгий запуск камеры:', targetDeviceId);
        
        // Увеличиваем количество попыток для ручного выбора (POCO fix)
        // Если это ручной выбор - долбимся до последнего (8 раз)
        // Если автоматический - 3 раза достаточно
        const maxAttempts = manualDeviceId ? 8 : 3;

        // Пытаемся открыть конкретную камеру
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
              console.log(`🔄 Попытка ${attempt}/${maxAttempts} (deviceId: ${targetDeviceId})`);
              
              stream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: targetDeviceId } },
              });
              
              // Проверка на всякий случай
              if (stream && stream.active) {
                console.log('✅ Камера успешно открыта!');
                break;
              } else {
                throw new Error('Stream inactive');
              }
            } catch (e) {
              console.warn(`❌ Ошибка попытки ${attempt}:`, e.name, e.message);
              
              // Очистка если вдруг открылось, но криво
              if (stream) {
                 stream.getTracks().forEach(t => t.stop());
                 stream = null;
              }

              if (attempt < maxAttempts) {
                // Прогрессивная задержка: 500, 1000, 1500, 2000...
                // POCO нужно время чтобы "отпустить" железо
                const delay = 500 + (attempt * 400); 
                console.log(`⏳ Ждем ${delay}мс перед повтором...`);
                await new Promise(r => setTimeout(r, delay));
              }
            }
        }

        // ВАЖНО: Если это был явный выбор камеры (manualDeviceId) -> НЕ ДЕЛАЕМ ФОЛБЭК
        // Чтобы пользователя не "перекинуло" на другую камеру.
        if (!stream && manualDeviceId) {
             console.warn('⛔ Не удалось открыть выбранную камеру. Фолбэк запрещен.');
             throw new Error(tr("Не удалось подключиться к выбранной камере. Попробуйте другую."));
        } 
        
        // Если это был авто-выбор, то пробуем fallback
        if (!stream) {
          console.log('⚠️ Все попытки deviceId провалились, пробуем fallback-камеру');
          try {
             stream = await navigator.mediaDevices.getUserMedia({ video: true });
          } catch(err) { /* ignore */ }
        }
      }
      // ==== ПЕРВЫЙ ЗАПУСК ====
      else {
        console.log('🚀 Первый запуск: поиск задней камеры');
        setIsCameraLoading(true);

        // 1. Сначала пробуем localStorage (сохранённый deviceId задней камеры)
        let preferredId = null;
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
             preferredId = window.localStorage.getItem('preferredCameraDeviceId');
          }
        } catch(e) {}

        if (preferredId) {
             console.log('💾 Пробуем сохранённую камеру:', preferredId);
             try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: { deviceId: { exact: preferredId } }
                });
             } catch(e) {
                console.warn('❌ Сохранённая камера не открылась:', e.name);
                preferredId = null;
             }
        }

        // 2. «Probe & Switch» — надёжный поиск задней камеры
        if (!stream) {
            // Шаг 2а: запрашиваем разрешение с подсказкой environment
            let probeStream = null;
            try {
                // Строгий запрос: exact environment (работает на большинстве Android)
                probeStream = await navigator.mediaDevices.getUserMedia({
                  video: { facingMode: { exact: 'environment' } }
                });
            } catch (_) {
                // Если exact не сработал — мягкий ideal
                try {
                   probeStream = await navigator.mediaDevices.getUserMedia({
                     video: { facingMode: { ideal: 'environment' } }
                   });
                } catch (_2) {
                    // Крайний fallback: любая камера
                    try {
                       probeStream = await navigator.mediaDevices.getUserMedia({ video: true });
                    } catch (fatal) {
                       throw fatal;
                    }
                }
            }

            // Шаг 2б: получаем метки устройств (они доступны только ПОСЛЕ получения разрешения)
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(d => d.kind === 'videoinput');
            videoDevicesRef.current = videoDevices;
            setAvailableCameras(videoDevices.map(d => ({
              id: d.deviceId,
              label: d.label || tr("Неизвестная"),
              deviceId: d.deviceId,
            })));

            // Шаг 2в: выбираем лучшую заднюю камеру из списка
            // Приоритет: POCO "camera2 0" → "back/rear/задн/основн" → не-фронтальная → последняя
            const probeTrack = probeStream.getVideoTracks()[0];
            const probeDeviceId = probeTrack.getSettings().deviceId;
            const probeFacing = probeTrack.getSettings().facingMode;

            let targetIdx = -1;

            // POCO-специфика
            targetIdx = videoDevices.findIndex(d => /camera2 0/i.test(d.label || ''));

            // Общие ключевые слова
            if (targetIdx === -1) {
                targetIdx = videoDevices.findIndex(d =>
                  /back|rear|environment|задн|основн/i.test(d.label || '')
                );
            }

            // Если камер > 1 и первая похожа на фронтальную — берём последнюю
            if (targetIdx === -1 && videoDevices.length > 1) {
                if (/front|selfie|передн|user/i.test(videoDevices[0].label || '')) {
                     targetIdx = videoDevices.length - 1;
                }
            }

            // Если probe уже открыл заднюю (facingMode environment) — не перепрыгиваем
            if (targetIdx !== -1 && videoDevices[targetIdx].deviceId === probeDeviceId) {
                 targetIdx = -1; // уже правильная камера
            }

            if (targetIdx !== -1) {
                 console.log('🔎 Probe не то. Переключаемся на:', videoDevices[targetIdx].label);
                 probeTrack.stop();
                 probeStream.getTracks().forEach(t => t.stop());
                 await new Promise(r => setTimeout(r, 300));

                 try {
                     stream = await navigator.mediaDevices.getUserMedia({
                         video: { deviceId: { exact: videoDevices[targetIdx].deviceId } }
                     });
                     cameraIndexRef.current = targetIdx;
                 } catch(e) {
                     console.warn('❌ Целевая камера не открылась, используем probe-фолбэк');
                     // Переоткрываем задний режим без exact
                     try {
                       stream = await navigator.mediaDevices.getUserMedia({
                         video: { facingMode: { ideal: 'environment' } }
                       });
                     } catch (_) {
                       stream = await navigator.mediaDevices.getUserMedia({ video: true });
                     }
                 }
            } else {
                 console.log('✅ Probe уже открыл нужную камеру (', probeFacing, ')');
                 stream = probeStream;
                 const foundIdx = videoDevices.findIndex(d => d.deviceId === probeDeviceId);
                 cameraIndexRef.current = foundIdx !== -1 ? foundIdx : 0;
            }
        }
      }

      // Подключаем stream к video
      if (videoRef.current && stream) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        videoRef.current.setAttribute('playsinline', true);
        videoRef.current.setAttribute('autoplay', true);
        videoRef.current.setAttribute('muted', true);

        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const settings = videoTrack.getSettings();
          const camInfo = {
            label: videoTrack.label || tr("Неизвестная камера"),
            facingMode: settings.facingMode || tr("неизвестно"),
            width: settings.width,
            height: settings.height,
            deviceId: settings.deviceId,
          };
          setCurrentCameraInfo(camInfo);

          // Сохраняем "удачную" заднюю камеру
          try {
            if (typeof window !== 'undefined' && window.localStorage && camInfo.deviceId) {
              const labelLower = (camInfo.label || '').toLowerCase();
              const isBackLike = /camera2 0|back|rear|environment|задн|основн/i.test(labelLower);
              if (isBackLike) {
                window.localStorage.setItem('preferredCameraDeviceId', camInfo.deviceId);
              }
            }
          } catch (e) {}
        }

        // Ждём готовности видео
        try {
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error(tr("Таймаут загрузки видео"))), 15000);
            videoRef.current.onloadedmetadata = () => {
              clearTimeout(timeout);
              videoRef.current.play().then(resolve).catch(reject);
            };
            videoRef.current.onerror = (err) => reject(err);
          });
        } catch (playError) {
          console.error('Не удалось запустить видео:', playError);
          throw new Error(tr("Не удалось запустить видео с камеры"));
        }

        scanningRef.current = true;
        requestAnimationFrame(scanQRCode);
        onNotify({ isOpen: true, message: tr("Камера успешно запущена"), type: 'success' });
      }
    } catch (err) {
      console.error('Ошибка камеры:', err);
      let errorMessage = err.message || tr("Ошибка камеры");
      if (err.name === 'NotAllowedError') errorMessage = tr("Доступ к камере запрещен.");

      onNotify({ isOpen: true, message: errorMessage, type: 'error' });

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    } finally {
      setIsCameraLoading(false);
    }
  }, [scanQRCode, onNotify]);

  // ---- Остановка камеры ----
  const stopCamera = useCallback(() => {
    scanningRef.current = false;
    setCurrentCameraInfo(null);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      videoRef.current.load();
    }
  }, []);

  // ---- Переключение камеры ----
  const switchCamera = useCallback(async () => {
    const vd = videoDevicesRef.current;
    if (vd.length <= 1) return;

    const nextIndex = (cameraIndexRef.current + 1) % vd.length;
    cameraIndexRef.current = nextIndex;
    
    stopCamera();
    await new Promise(resolve => setTimeout(resolve, 1000));
    if (showQr) {
      await startCamera();
    }
  }, [stopCamera, startCamera, showQr]);

  // ---- Выбор конкретной камеры ----
  const selectCamera = useCallback(async (deviceId) => {
    const vd = videoDevicesRef.current;
    const index = vd.findIndex(d => d.deviceId === deviceId);
    if (index !== -1) {
      console.log('🎯 Выбор камеры вручную:', vd[index].label);
      cameraIndexRef.current = index;
      stopCamera();
      // Увеличиваем паузу до 1.2c для гарантии сброса железа на слабых устройствах
      await new Promise(resolve => setTimeout(resolve, 1200));
      if (showQr) {
        // Pass deviceId to force strict mode
        await startCamera(deviceId);
      }
    }
  }, [stopCamera, startCamera, showQr]);

  // ---- Lifecycle: запуск/остановка по showQr ----
  useEffect(() => {
    let mounted = true;
    const initCamera = async () => {
      if (showQr && mounted) {
        stopCamera();
        await new Promise(resolve => setTimeout(resolve, 100));
        if (mounted) await startCamera();
      } else {
        stopCamera();
      }
    };
    initCamera();
    return () => {
      mounted = false;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQr]);

  return {
    videoRef,
    canvasRef,
    streamRef,
    scanningRef,
    availableCameras,
    currentCameraInfo,
    isCameraLoading,
    startCamera,
    stopCamera,
    switchCamera,
    selectCamera,
    cameraIndexRef,
    videoDevicesRef,
  };
};

export default useCamera;
